import type { CreativeDecisionProjection } from './contracts';

export const CREATIVE_DECISION_COPILOT_INTENTS = [
  'explain_performance',
  'review_next_experiment',
  'assess_trust',
  'summarize_support',
] as const;

export type CreativeDecisionCopilotIntent = typeof CREATIVE_DECISION_COPILOT_INTENTS[number];

export type CreativeDecisionCopilotRefusalCode =
  | 'evidence_snapshot_missing'
  | 'evidence_hash_mismatch'
  | 'evidence_stale'
  | 'evidence_incomplete';

export interface CreativeDecisionCopilotMetricEvidence {
  metricKey: string;
  evidenceClass: 'descriptive' | 'predictive' | 'randomized' | 'incremental';
  measurementBasis: string;
  scopeRef: string;
  counterfactualAvailable: boolean;
  sourceRefs: string[];
  asOf: string;
  freshnessState: 'fresh' | 'stale' | 'unknown';
  coverage?: {
    numerator: number;
    denominator: number;
    unit: string;
  };
}

export interface CreativeDecisionCopilotAnswer {
  answerId: string;
  projectId: string;
  snapshotId: string;
  projectionHash: string;
  snapshotVerified: boolean;
  intent: CreativeDecisionCopilotIntent;
  /** Canonical server-classified question. Raw caller text is never persisted. */
  question: string;
  conclusionAvailable: boolean;
  answer: string;
  freshness: 'fresh' | 'partial' | 'stale' | 'unavailable';
  metricEvidence: CreativeDecisionCopilotMetricEvidence[];
  sourceRefs: string[];
  uncertainty: string;
  alternativeExplanations: string[];
  blockedGates: string[];
  nextExperiment?: string;
  refusalCode?: CreativeDecisionCopilotRefusalCode;
  humanReviewRequired: true;
  providerMutationAllowed: false;
  experimentTransitionAllowed: false;
  contentHash: string;
  answeredAt: string;
}

export interface CreativeDecisionCopilotAnswerRequest {
  snapshotId: string;
  projectionHash: string;
  question: string;
  signal?: AbortSignal;
}

export interface CreativeDecisionCopilotSnapshotMaterial {
  snapshotId: string;
  projectionVersion: string;
  sourceRevisionRefs: string[];
  asOf: string;
  freshnessState: 'fresh' | 'stale' | 'unknown';
  uncertainty: string;
  alternativeExplanations: string[];
  blockedGates: string[];
  nextExperiment?: string;
}

/**
 * Builds bounded server-side snapshot material. It deliberately does not
 * collapse metric evidence into one strongest class: every answer must retain
 * the exact class on each metric binding.
 */
export function buildCreativeDecisionCopilotSnapshot(
  decision: CreativeDecisionProjection,
  evaluatedAt = new Date().toISOString(),
): CreativeDecisionCopilotSnapshotMaterial {
  const sourceRevisionRefs = unique([
    ...decision.freshness.sourceRevisionRefs,
    ...decision.evidence.flatMap((binding) => binding.sourceRefs),
  ]);
  const freshnessState = deriveFreshnessState(decision, evaluatedAt);
  const blockedGates = unique([
    ...(decision.dataState === 'available' ? [] : decision.dataStateReasonCodes),
    ...decision.recommendation.reasonCodes,
    ...unavailableSupportReasons(decision),
  ]);
  const suggestedExperiment = nextExperiment(decision);

  return {
    snapshotId: decision.projectionId,
    projectionVersion: decision.projectionVersion,
    sourceRevisionRefs,
    asOf: decision.freshness.asOf,
    freshnessState,
    uncertainty: uncertaintySummary(decision),
    alternativeExplanations: alternativeExplanations(decision),
    blockedGates,
    ...(suggestedExperiment ? { nextExperiment: suggestedExperiment } : {}),
  };
}

/** Strict allowlist parser for the backend-bound copilot response. */
export function parseCreativeDecisionCopilotAnswer(
  value: unknown,
  expected: { projectId: string; snapshotId: string; projectionHash: string },
): CreativeDecisionCopilotAnswer {
  const record = object(value, 'copilot answer');
  exactKeys(record, [
    'answerId', 'projectId', 'snapshotId', 'projectionHash', 'snapshotVerified', 'intent', 'question',
    'conclusionAvailable', 'answer', 'freshness', 'metricEvidence', 'sourceRefs', 'uncertainty',
    'alternativeExplanations', 'blockedGates', 'nextExperiment', 'refusalCode', 'humanReviewRequired',
    'providerMutationAllowed', 'experimentTransitionAllowed', 'contentHash', 'answeredAt',
  ], 'copilot answer');
  assertNoPrivatePayload(record);
  for (const [field, expectedValue] of Object.entries(expected)) {
    if (record[field] !== expectedValue) throw invalid(`copilot answer ${field} does not match the request.`);
  }
  text(record.answerId, 'answerId', 128);
  text(record.projectId, 'projectId', 512);
  text(record.snapshotId, 'snapshotId', 128);
  hash(record.projectionHash, 'projectionHash');
  if (typeof record.snapshotVerified !== 'boolean') throw invalid('snapshotVerified must be boolean.');
  if (!CREATIVE_DECISION_COPILOT_INTENTS.includes(record.intent as CreativeDecisionCopilotIntent)) {
    throw invalid('intent is unsupported.');
  }
  text(record.question, 'question', 500);
  if (typeof record.conclusionAvailable !== 'boolean') throw invalid('conclusionAvailable must be boolean.');
  text(record.answer, 'answer', 8_000);
  if (!['fresh', 'partial', 'stale', 'unavailable'].includes(String(record.freshness))) {
    throw invalid('freshness is unsupported.');
  }
  const metricEvidence = array(record.metricEvidence, 'metricEvidence', 100).map((entry, index) => (
    parseMetricEvidence(entry, `metricEvidence[${index}]`)
  ));
  const sourceRefs = stringArray(record.sourceRefs, 'sourceRefs', 200, 2_048);
  text(record.uncertainty, 'uncertainty', 1_000);
  stringArray(record.alternativeExplanations, 'alternativeExplanations', 20, 500);
  stringArray(record.blockedGates, 'blockedGates', 100, 512);
  if (record.nextExperiment !== undefined) text(record.nextExperiment, 'nextExperiment', 500);
  if (record.refusalCode !== undefined && ![
    'evidence_snapshot_missing', 'evidence_hash_mismatch', 'evidence_stale', 'evidence_incomplete',
  ].includes(String(record.refusalCode))) throw invalid('refusalCode is unsupported.');
  if (record.humanReviewRequired !== true || record.providerMutationAllowed !== false
    || record.experimentTransitionAllowed !== false) {
    throw invalid('copilot safety invariants are invalid.');
  }
  hash(record.contentHash, 'contentHash');
  isoTimestamp(record.answeredAt, 'answeredAt');
  if (record.conclusionAvailable === (record.refusalCode !== undefined)) {
    throw invalid('conclusionAvailable and refusalCode are inconsistent.');
  }
  if (record.conclusionAvailable && (!record.snapshotVerified || metricEvidence.length === 0)) {
    throw invalid('a conclusion requires a resolved snapshot and metric evidence.');
  }
  const boundSources = unique(metricEvidence.flatMap((binding) => binding.sourceRefs));
  if (record.conclusionAvailable && boundSources.some((ref) => !sourceRefs.includes(ref))) {
    throw invalid('sourceRefs must include every selected metric source.');
  }
  return record as unknown as CreativeDecisionCopilotAnswer;
}

function parseMetricEvidence(value: unknown, path: string): CreativeDecisionCopilotMetricEvidence {
  const record = object(value, path);
  exactKeys(record, [
    'metricKey', 'evidenceClass', 'measurementBasis', 'scopeRef', 'counterfactualAvailable',
    'sourceRefs', 'asOf', 'freshnessState', 'coverage',
  ], path);
  text(record.metricKey, `${path}.metricKey`, 256);
  if (!['descriptive', 'predictive', 'randomized', 'incremental'].includes(String(record.evidenceClass))) {
    throw invalid(`${path}.evidenceClass is unsupported.`);
  }
  text(record.measurementBasis, `${path}.measurementBasis`, 512);
  text(record.scopeRef, `${path}.scopeRef`, 2_048);
  if (typeof record.counterfactualAvailable !== 'boolean') throw invalid(`${path}.counterfactualAvailable must be boolean.`);
  stringArray(record.sourceRefs, `${path}.sourceRefs`, 50, 2_048);
  isoTimestamp(record.asOf, `${path}.asOf`);
  if (!['fresh', 'stale', 'unknown'].includes(String(record.freshnessState))) {
    throw invalid(`${path}.freshnessState is unsupported.`);
  }
  if (record.coverage !== undefined) {
    const coverage = object(record.coverage, `${path}.coverage`);
    exactKeys(coverage, ['numerator', 'denominator', 'unit'], `${path}.coverage`);
    if (!Number.isFinite(coverage.numerator) || !Number.isFinite(coverage.denominator)
      || Number(coverage.denominator) <= 0 || Number(coverage.numerator) < 0
      || Number(coverage.numerator) > Number(coverage.denominator)) {
      throw invalid(`${path}.coverage is invalid.`);
    }
    text(coverage.unit, `${path}.coverage.unit`, 128);
  }
  return record as unknown as CreativeDecisionCopilotMetricEvidence;
}

function deriveFreshnessState(
  decision: CreativeDecisionProjection,
  evaluatedAt: string,
): CreativeDecisionCopilotSnapshotMaterial['freshnessState'] {
  if (!decision.freshness.sourceRevisionRefs.length || !decision.evidence.length) return 'unknown';
  if (decision.evidence.some((binding) => binding.freshnessState !== 'fresh')) return 'stale';
  const evaluated = Date.parse(evaluatedAt);
  const expires = decision.freshness.expiresAt ? Date.parse(decision.freshness.expiresAt) : null;
  if (expires !== null && Number.isFinite(expires) && Number.isFinite(evaluated) && evaluated >= expires) return 'stale';
  return 'fresh';
}

function unavailableSupportReasons(decision: CreativeDecisionProjection): string[] {
  const support = decision.decisionSupport;
  if (!support) return ['decision_support_unavailable'];
  return [
    support.predictionTrust,
    support.randomizedOrFactorialInteraction,
    support.validatedSurrogate,
    support.incrementality,
    support.guardrails,
    support.fatigue,
  ].flatMap((item) => item?.status === 'unavailable' ? item.reasonCodes : []);
}

function uncertaintySummary(decision: CreativeDecisionProjection): string {
  const { projectedContributionRoasP10, projectedContributionRoasP50, projectedContributionRoasP90 } = decision.money;
  if (
    Number.isFinite(projectedContributionRoasP10)
    && Number.isFinite(projectedContributionRoasP50)
    && Number.isFinite(projectedContributionRoasP90)
  ) {
    return `projected contribution ROAS interval P10 ${formatRoas(projectedContributionRoasP10 as number)}, P50 ${formatRoas(projectedContributionRoasP50 as number)}, P90 ${formatRoas(projectedContributionRoasP90 as number)}`;
  }
  if (decision.evidence.some((binding) => binding.evidenceClass === 'randomized' || binding.evidenceClass === 'incremental')) {
    return 'qualified evidence is present, but no compatible numeric uncertainty interval is exposed in this projection';
  }
  return 'no decision-eligible counterfactual interval is available';
}

function alternativeExplanations(decision: CreativeDecisionProjection): string[] {
  return unique([
    ...(decision.evidence.some((binding) => !binding.counterfactualAvailable)
      ? ['provider optimization, targeting, placement mix, or campaign budget may explain observed performance']
      : []),
    ...(decision.artifactContext?.heterogeneityState === 'divergent'
      ? ['the same artifact has divergent outcomes in other deployment contexts']
      : []),
    ...(decision.capability.firstPartyJoinGrain === 'unavailable'
      ? ['provider-attributed outcomes are not joined to first-party contribution value']
      : []),
    ...(decision.money.projectedValueBasis?.availability === 'unavailable'
      ? ['projected contribution is unavailable for the current value-basis coverage']
      : []),
  ]);
}

function nextExperiment(decision: CreativeDecisionProjection): string | undefined {
  const registered = decision.decisionSupport?.nextExperiment;
  if (registered?.status === 'available') {
    return [registered.experimentKind?.replace(/_/g, ' '), registered.experimentId]
      .filter(Boolean)
      .join(' · ') || 'review the registered next experiment';
  }
  switch (decision.recommendation.primaryAction) {
    case 'create_refresh_experiment': return 'a reviewed creative refresh experiment';
    case 'review_scale_probe': return 'a bounded, pre-registered scale probe';
    case 'review_stop_evidence': return 'a pre-registered stop-evidence review';
    case 'review_next_read': return 'a pre-registered next-read plan';
    default: return undefined;
  }
}

function exactKeys(value: Record<string, unknown>, allowed: string[], label: string): void {
  const allowlist = new Set(allowed);
  const unknown = Object.keys(value).filter((key) => !allowlist.has(key)).sort();
  if (unknown.length) throw invalid(`Unknown ${label} fields: ${unknown.join(', ')}.`);
}

function assertNoPrivatePayload(value: unknown, path = '$'): void {
  const privateKey = /(?:^|_)(?:access_?token|refresh_?token|password|secret|credential|private_?key|email|raw_?(?:answer|event|payload)|storage_?path|signed_?url)(?:$|_)/i;
  const privateValue = /(?:-----BEGIN [A-Z ]+PRIVATE KEY-----|\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b|\+\d[\d .()-]{7,}\d|(?:gs|s3):\/\/|\/Users\/|\/home\/|\/var\/folders\/)/i;
  if (typeof value === 'string') {
    if (privateValue.test(value)) throw invalid(`Private value rejected at ${path}.`);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((item, index) => assertNoPrivatePayload(item, `${path}[${index}]`));
    return;
  }
  if (!value || typeof value !== 'object') return;
  for (const [key, nested] of Object.entries(value as Record<string, unknown>)) {
    if (privateKey.test(key.replace(/([a-z0-9])([A-Z])/g, '$1_$2'))) {
      throw invalid(`Private field rejected at ${path}.${key}.`);
    }
    assertNoPrivatePayload(nested, `${path}.${key}`);
  }
}

function object(value: unknown, field: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw invalid(`${field} must be an object.`);
  return value as Record<string, unknown>;
}

function array(value: unknown, field: string, max: number): unknown[] {
  if (!Array.isArray(value) || value.length > max) throw invalid(`${field} must be an array of at most ${max} items.`);
  return value;
}

function stringArray(value: unknown, field: string, maxItems: number, maxLength: number): string[] {
  return array(value, field, maxItems).map((item, index) => text(item, `${field}[${index}]`, maxLength));
}

function text(value: unknown, field: string, max: number): string {
  if (typeof value !== 'string' || !value.trim() || value.length > max || /[\u0000-\u001F\u007F]/u.test(value)) {
    throw invalid(`${field} must be bounded non-control text.`);
  }
  return value;
}

function hash(value: unknown, field: string): string {
  if (typeof value !== 'string' || !/^[a-f0-9]{64}$/.test(value)) throw invalid(`${field} must be SHA-256.`);
  return value;
}

function isoTimestamp(value: unknown, field: string): string {
  const result = text(value, field, 64);
  if (!Number.isFinite(Date.parse(result))) throw invalid(`${field} must be an ISO timestamp.`);
  return result;
}

function invalid(message: string): Error {
  return new Error(`Creative decision copilot response failed validation: ${message}`);
}

function formatRoas(value: number): string {
  return `${value.toFixed(2)}x`;
}

function unique(values: string[]): string[] {
  return [...new Set(values.filter(Boolean))].sort();
}
